import React from 'react';
import { Search, Filter, Star, ShoppingBag, ArrowRight } from 'lucide-react';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';

const SHOES_DATA = [
    { id: "nike-air-max-90", model: "Nike Air Max 90", brand: "Nike", price: 479.00, original_price: 699.00, image_url: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=300&fit=crop", shop: "Zalando", rating: 4.7, reviews: 2847 },
    { id: "adidas-ultraboost-22", model: "Adidas Ultraboost 22", brand: "Adidas", price: 599.00, original_price: 749.00, image_url: "https://images.unsplash.com/photo-1600185365483-26d7a4cc7519?w=400&h=300&fit=crop", shop: "Eobuwie", rating: 4.6, reviews: 1932 },
    { id: "jordan-1-retro", model: "Air Jordan 1 Retro High", brand: "Jordan", price: 899.00, original_price: 1099.00, image_url: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop", shop: "Footlocker", rating: 4.8, reviews: 2156 },
    { id: "new-balance-550", model: "New Balance 550", brand: "New Balance", price: 449.00, original_price: 549.00, image_url: "https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=400&h=300&fit=crop", shop: "Sportano", rating: 4.4, reviews: 1243 },
];

export const SizeHunterAiPage: React.FC = () => {
    usePageMetadata({
        title: "SizeHunter AI | Porównywarka Cen i Rozmiarów Butów",
        description: "Znajdź swój rozmiar butów w najlepszej cenie. SizeHunter AI porównuje ceny i dostępność w 150+ sklepach w czasie rzeczywistym. Sprawdź teraz!",
        schema: {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "SizeHunter AI (Agregator Sklepów)",
            "serviceType": "Porównywarka Cen i Dostępności Produktów",
            "provider": {
                "@type": "Organization",
                "name": "BiznesBezKlikania.pl"
            },
            "description": "Agregator cen i rozmiarów butów z ponad 150 sklepów. Monitoruje dostępność w czasie rzeczywistym, pomagając znaleźć najlepsze oferty.",
            "url": "https://biznesbezklikania.pl/rozwiazania/sizehunter-ai"
        }
    });

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[50vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="purple" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
                            <ShoppingBag className="w-10 h-10 text-white" />
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            SizeHunter AI
                        </h1>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto">
                            Znajdź swój rozmiar w najlepszej cenie - porównujemy 150+ sklepów w czasie rzeczywistym.
                        </p>
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* Search Section */}
            <section className="py-8 relative">
                <div className="container mx-auto px-4">
                    <RevealOnScroll>
                        <GlassCard variant="strong" className="mb-8">
                            <div className="flex flex-col lg:flex-row gap-4">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary-light dark:text-text-secondary-dark w-6 h-6" />
                                    <input
                                        type="text"
                                        placeholder="Wyszukaj buty po marce, modelu..."
                                        className="w-full pl-12 pr-4 py-3 border border-white/20 dark:border-white/10 rounded-xl bg-white/50 dark:bg-white/5 backdrop-blur-sm focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none transition-all text-text-primary-light dark:text-text-primary-dark"
                                    />
                                </div>
                                <button className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 dark:bg-white/5 border border-white/20 dark:border-white/10 rounded-xl font-semibold hover:bg-white/20 dark:hover:bg-white/10 transition-all text-text-primary-light dark:text-text-primary-dark">
                                    <Filter className="w-5 h-5" />
                                    Filtry
                                </button>
                            </div>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-12 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll>
                        <GlassCard variant="strong" className="max-w-4xl mx-auto text-center">
                            <h2 className="text-2xl font-bold mb-4 text-text-primary-light dark:text-text-primary-dark">
                                Jak to działa? Twoja przewaga w polowaniu na okazje.
                            </h2>
                            <p className="text-text-secondary-light dark:text-text-secondary-dark">
                                Nasze AI skanuje ponad 150 sklepów obuwniczych co 5 minut, analizując ceny, dostępne rozmiary i ukryte promocje. Dzięki temu masz pewność, że znajdujesz najlepszą ofertę na rynku bez spędzania godzin na ręcznym przeglądaniu stron. To nie jest zwykła porównywarka – to Twój osobisty asystent zakupowy.
                            </p>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </section>

            {/* Products Grid */}
            <section className="py-12 relative">
                <div className="container mx-auto px-4 relative z-10">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {SHOES_DATA.map((shoe, index) => (
                            <RevealOnScroll key={shoe.id} delay={index * 100}>
                                <GlassCard variant="medium" hover className="overflow-hidden p-0">
                                    <img
                                        src={shoe.image_url}
                                        alt={`${shoe.brand} ${shoe.model}`}
                                        className="w-full h-48 object-cover"
                                        loading="lazy"
                                    />
                                    <div className="p-4">
                                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">{shoe.brand}</p>
                                        <h3 className="font-bold text-lg text-text-primary-light dark:text-text-primary-dark">{shoe.model}</h3>
                                        <div className="flex justify-between items-center mt-4">
                                            <div>
                                                <p className="text-2xl font-bold text-primary-light dark:text-primary-dark">{shoe.price.toFixed(2)} zł</p>
                                                <p className="text-sm text-red-500 line-through">{shoe.original_price.toFixed(2)} zł</p>
                                            </div>
                                            <div className="text-right">
                                                <div className="flex items-center justify-end">
                                                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                                                    <span className="ml-1 text-sm text-text-primary-light dark:text-text-primary-dark">{shoe.rating} ({shoe.reviews})</span>
                                                </div>
                                                <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">{shoe.shop}</p>
                                            </div>
                                        </div>
                                    </div>
                                </GlassCard>
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            <WhatNextSection diyLink="/rozwiazania/sizehunter-ai" />
        </>
    );
};
